import { Stack } from '@mui/material';
import { isMeteredCreditProductOnly, isNotMeteredCreditProductOnly } from '@vy/services';
import type { UsageProductInput } from '@vy/types';
import { BILLING_INTERVALS, DEFAULT_CURRENCY } from '@vy/types';
import {
  getEndOfPreviousDay,
  isNotEmptyArray,
  isNotNullOrUndefined,
  isNullOrUndefined,
  Period,
  vyDate,
  withDefault,
} from '@vy/utils';
import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { getSplitVolumeUsageProducts } from '../../../../../utils/get-split-usage-products';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { FixedProductsSection } from './create-plan-live-preview-components/FixedProductsSection';
import { LivePreviewContainer } from './create-plan-live-preview-components/LivePreviewContainer';
import { PromotionsSection } from './create-plan-live-preview-components/PromotionsSection';
import { UsageProductsSection } from './create-plan-live-preview-components/UsageProductsSection';
import { usePlanTotalAmount } from './create-plan-live-preview-hooks/use-plan-total-amount';
import { EmptyLivePreview } from './create-plan-live-preview-components/EmptyLivePreview';
import { ProductTimelineChartDialog } from './create-plan-live-preview-components/ProductTimelineChartDialog/ProductTimelineChartDialog';

type CreatePlanLivePreviewProps = {
  title: React.ReactNode;
  onHide?: () => void;
};

// TODO: change name to CreateLivePreview or something else since this component is being use both in CreatePlanPage and CreateInvoicePage
export const CreatePlanLivePreview: React.FC<CreatePlanLivePreviewProps> = ({ title, onHide }) => {
  const totalAmount = usePlanTotalAmount();
  const { watch } = useFormContext<PlanInputForm>();

  const billingData = watch('billingData') ?? {};
  const startDate = watch('startDate');
  const duration = watch('billingData.duration');
  const billingInterval = withDefault(watch('billingData.billingInterval'), BILLING_INTERVALS.Monthly);
  const fixedProducts = withDefault(watch('fixedProducts'), []);
  const usageProducts = withDefault(watch('usageProducts'), []);
  const promotionProducts = withDefault(watch('promotionProducts'), []);

  const meteredCredits = usageProducts.filter(isMeteredCreditProductOnly);
  const usageProductsWithoutMeteredCredits = usageProducts.filter(isNotMeteredCreditProductOnly);

  const products = [...fixedProducts, ...usageProducts];
  const promotions = [...promotionProducts];

  const itemsCount = products.length + promotions.length;
  const productsExists = itemsCount > 0;

  const { nonVolumeBasedProducts, volumeBasedProducts } = getSplitVolumeUsageProducts(
    usageProductsWithoutMeteredCredits as UsageProductInput[],
  );

  const contractPeriod = useMemo(() => {
    return buildContractPeriod(startDate, duration);
  }, [startDate, duration]);

  const displayTimelineDialog = isNotEmptyArray(products) && isNotNullOrUndefined(contractPeriod);

  return (
    <LivePreviewContainer
      title={title}
      currency={DEFAULT_CURRENCY}
      itemsCount={itemsCount}
      totalAmount={totalAmount}
      onHide={onHide}
    >
      {!productsExists ? (
        <EmptyLivePreview />
      ) : (
        <Stack
          direction="column"
          sx={{
            rowGap: 3,
            padding: 3
          }}>
          {displayTimelineDialog && (
            <ProductTimelineChartDialog contractPeriod={contractPeriod} products={products} />
          )}
          {isNotEmptyArray(fixedProducts) && (
            <FixedProductsSection
              fixedProducts={fixedProducts}
              billingData={billingData}
              currency={DEFAULT_CURRENCY}
            />
          )}

          {isNotEmptyArray(nonVolumeBasedProducts) && (
            <UsageProductsSection
              usageProducts={nonVolumeBasedProducts}
              billingInterval={billingInterval}
              currency={DEFAULT_CURRENCY}
            />
          )}

          {isNotEmptyArray(volumeBasedProducts) && (
            <UsageProductsSection
              usageProducts={volumeBasedProducts}
              title="Usage-based Fees - Volume pricing"
              billingInterval={billingInterval}
              currency={DEFAULT_CURRENCY}
            />
          )}

          {isNotEmptyArray(meteredCredits) && (
            <UsageProductsSection
              usageProducts={meteredCredits}
              title="Metered credits"
              billingInterval={billingInterval}
              currency={DEFAULT_CURRENCY}
            />
          )}

          {isNotEmptyArray(promotions) && (
            <PromotionsSection promotions={promotions} currency={DEFAULT_CURRENCY} />
          )}
        </Stack>
      )}
    </LivePreviewContainer>
  );
};

function buildContractPeriod(startTime: Date | undefined, duration: number | undefined): Period | undefined {
  if (isNullOrUndefined(startTime) || isNullOrUndefined(duration)) {
    return undefined;
  }

  const startWithAddedDuration = vyDate(startTime).add(duration, 'months').toDate();
  const endTime = getEndOfPreviousDay(startWithAddedDuration);

  return { startTime, endTime };
}
